import React from "react"

import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons"
import { Form as AntdForm, Button, Card, Input, message, Popconfirm, PopconfirmProps, Select, Typography } from "antd"
import { useEffect, useState } from "react"
import { ReactSortable, SortableEvent } from "react-sortablejs"
import { HttpClient } from "~/api/HttpClient"
import { t } from "~/languages/i18n"
import { CategoryItem, MenuItem, MenuPreference, PageItem, PostItem } from "~/types/types"
import menuService from "../service/menuService"
import CategoriesItem from "./CategoriesItem"
import { ContentHomePage } from "./Content"
import CustomLinksItem from "./CustomLinksItem"
import LanguageItem from "./LanguageItem"
import PagesItem from "./PagesItem"
import PostsItem from "./PostsItem"

const { Title } = Typography

const MenusListing: React.FC = () => {
  const [form] = AntdForm.useForm()
  const [mainTab, setMainTab] = useState("tab1")
  const [subTab, setSubTab] = useState("tabMos")
  const [expandedPages, setExpandedPages] = useState(true)
  const [expandedPosts, setExpandedPosts] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState(false)
  const [expandedCustomlink, setExpandedCustomlink] = useState(false)
  const [mostRecentPages, setMostRecentPages] = useState<PageItem[]>([])
  const [mostRecentPosts, setMostRecentPosts] = useState<PostItem[]>([])
  const [mostRecentCategories, setMostRecentCategories] = useState<CategoryItem[]>([])
  const [menuPreference, setMenuPreference] = useState<MenuPreference>()
  const [menuItems, setMenuItems] = useState<MenuItem[]>([])
  const [selectedMenu, setSelectedMenu] = useState<string | null>(null)
  const [expandedItem, setExpandedItem] = useState<string | null>(null)
  const [checkedItems, setCheckedItems] = useState<string[]>([])
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set())
  const [selectedPosts, setSelectedPosts] = useState<Set<number>>(new Set())
  const [selectedCategories, setSelectedCategories] = useState<Set<number>>(new Set())
  const [menuName, setMenuName] = useState("")
  const [isCreatingNewMenu, setIsCreatingNewMenu] = useState(false)
  const [startX, setStartX] = useState(0)
  const [language, setLanguage] = useState("")
  const [isLoadingLanguage, setIsLoadingLanguage] = useState(false)

  const API_PREFIX_MENUPREFERENCE_PATH = "/api/menu-preferences"

  const handleSelectMenuChange = async (value: string) => {
    if (value === "new") {
      setMenuItems([])
      setMenuName("")
      setIsCreatingNewMenu(true)
      return
    }

    setSelectedMenu(value)
    setIsCreatingNewMenu(false)
    setIsLoadingLanguage(true)
    try {
      const selectedItem = Array.isArray(menuPreference)
        ? menuPreference.find(item => item.id === parseInt(value))
        : undefined

      if (selectedItem) {
        setMenuName(selectedItem.attributes.name)
        setMenuItems(selectedItem.attributes.items || [])
        localStorage.setItem("selectedMenu", selectedItem.id)

        await new Promise(resolve => setTimeout(resolve, 100))
        setLanguage(selectedItem.attributes?.locale)
      }
    } catch (error) {
      console.error("Error setting language:", error)
    } finally {
      setIsLoadingLanguage(false)
    }
  }

  const toggleExpand = (id: string) => {
    setExpandedItem(expandedItem === id ? null : id)
  }

  const handleMainTabChange = (key: string) => {
    setMainTab(key)
  }

  const handleSubTabChange = (key: string) => {
    setSubTab(key)
  }

  const fetchPages = async () => {
    try {
      const response = await menuService.fetchAllPages()
      const data = response.data || []
      const items = data.map((page: { attributes: { name: string; slug: string } }) => ({
        name: page.attributes.name,
        slug: page.attributes.slug,
      }))
      setMostRecentPages(items)
    } catch (error) {
      message.error(t("Error fetching pages. Please try again."))
    }
  }

  const fetchPosts = async () => {
    try {
      const response = await menuService.fetchAllPost()
      const data = response.data || []
      const items = data.map((post: { attributes: { name: string; slug: string } }) => ({
        name: post.attributes.name,
        slug: post.attributes.slug,
      }))
      setMostRecentPosts(items)
    } catch (error) {
      message.error(t("Error fetching posts. Please try again."))
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await menuService.fetchAllCategories()
      const data = response.data || []
      const items = data.map((category: { attributes: { name: string; slug: string } }) => ({
        name: category.attributes.name,
        slug: category.attributes.slug,
      }))
      setMostRecentCategories(items)
    } catch (error) {
      message.error(t("Error fetching categories. Please try again."))
    }
  }

  const fetchMenuPreferences = async () => {
    try {
      const response = await menuService.fetchAllMenuPreference()
      const data = response.data

      if (data.length > 0) {
        setMenuPreference(data)
        const defaultItem = data[0].attributes

        if (defaultItem) {
          setSelectedMenu(data[0].id)
          const subItems = defaultItem.items || []
          setMenuItems(subItems)
        }
      } else {
        setMenuItems([])
      }
    } catch (error) {
      message.error(t("Error fetching menu preferences. Please try again."))
    }
  }

  const addToMenu = () => {
    let url = form.getFieldValue("url")
    const linkText = form.getFieldValue("linktext")

    const maxId = menuItems.reduce((max, item) => (item.id > max ? item.id : max), 0)

    const maxOrder = menuItems.reduce((max, item) => (item.order > max ? item.order : max), 0)

    let newMenuItems = []

    if (selectedMenu === "new") {
      setMenuItems([])
    }

    if (linkText && url) {
      const customLink = {
        id: maxId + 1,
        name: linkText || "No Title",
        slug: url || "#",
        parent: 0,
        order: maxOrder + 1,
      }

      const isDuplicate = menuItems.some(item => item.name === customLink.name && item.slug === customLink.slug)

      if (!isDuplicate) {
        newMenuItems.push(customLink)
      }
    }

    const newCheckedItems = checkedItems
      .map((item, index) => {
        let slug = ""

        const pageItem = mostRecentPages.find(page => page.name === item)
        if (pageItem) {
          slug = pageItem.slug
        } else {
          const postItem = mostRecentPosts.find(post => post.name === item)
          if (postItem) {
            slug = postItem.slug
          } else {
            const categoryItem = mostRecentCategories.find(category => category.name === item)
            slug = categoryItem ? categoryItem.slug : ""
          }
        }

        const newItem = {
          id: maxId + newMenuItems.length + index + 1,
          name: item,
          slug,
          parent: 0,
          order: maxOrder + 1 + index,
        }

        const isDuplicate = menuItems.some(menuItem => menuItem.name === newItem.name && menuItem.slug === newItem.slug)

        if (!isDuplicate) {
          return newItem
        }

        return null
      })
      .filter(item => item !== null)

    newMenuItems = [...newMenuItems, ...newCheckedItems]

    setMenuItems(prevMenuItems => [
      ...prevMenuItems,
      ...newMenuItems.map(item => ({
        ...item,
        url: item.slug,
      })),
    ])

    setCheckedItems([])
    setSelectedPosts(new Set())
    setSelectedCategories(new Set())
    form.resetFields(["url", "linktext"])
  }

  const saveMenu = async () => {
    const menuData = {
      name: menuName,
      items: menuItems,
      locale: language,
    }

    try {
      let response

      if (selectedMenu) {
        response = await HttpClient.put(`${API_PREFIX_MENUPREFERENCE_PATH}/${selectedMenu}`, {
          data: menuData,
        })
      } else {
        response = await HttpClient.post(`${API_PREFIX_MENUPREFERENCE_PATH}`, {
          data: menuData,
        })
      }

      const savedMenuId = selectedMenu || response.data.id
      setSelectedMenu(savedMenuId)
      localStorage.setItem("selectedMenu", savedMenuId)

      handleSelectMenuChange(savedMenuId)
      window.location.reload()
    } catch (error) {
      message.error(t("Failed to save menu. Please try again."))
    }
  }

  const handleDeleteMenu = async () => {
    try {
      await HttpClient.delete(`${API_PREFIX_MENUPREFERENCE_PATH}/${selectedMenu}`)
      message.success(t("Menu deleted successfully!"))

      const firstMenu = Array.isArray(menuPreference) && menuPreference.length > 0 ? menuPreference[0] : null

      if (firstMenu) {
        setSelectedMenu(firstMenu.id)
        setMenuName(firstMenu.attributes.name)
        setMenuItems(firstMenu.attributes.items || [])
        localStorage.setItem("selectedMenu", firstMenu.id)

        window.location.reload()
      } else {
        setSelectedMenu(null)
        setMenuName("")
        setMenuItems([])
        localStorage.removeItem("selectedMenu")
        message.info(t("No menus available."))
      }
    } catch (error) {
      message.error(t("Failed to delete menu. Please try again."))
    }
  }

  const handleRemove = (id: number) => {
    setMenuItems(prevMenuItems => {
      const updatedMenuItems = prevMenuItems.filter(item => item.id !== id)

      return updatedMenuItems.map(item => {
        if (item.id > id && item.parent !== null && item.parent > 0) {
          return { ...item, parent: item.parent - 1 }
        }
        return item
      })
    })
  }

  const handleStart = (event: SortableEvent) => {
    const { clientX } = (event as any).originalEvent
    setStartX(clientX)
  }

  const handleEnd = (event: SortableEvent) => {
    const { newIndex, oldIndex } = event
    const { clientX } = (event as any).originalEvent
    const updatedItems = [...menuItems]

    const minOrder = Math.min(...updatedItems.map(item => item.order))
    const movedItem = oldIndex !== undefined ? updatedItems[oldIndex] : null

    if (oldIndex !== undefined && updatedItems[oldIndex].order === minOrder) {
      return
    }

    const resultX = clientX - startX

    if (movedItem && movedItem.parent === 0 && resultX <= -40) {
      return
    }

    if (resultX >= 40) {
      if (oldIndex !== undefined && movedItem && movedItem.parent !== null) {
        const previousItem = updatedItems.find(item => item.order === movedItem.order - 1)
        if (previousItem && previousItem.parent !== null && movedItem.parent < previousItem.parent + 1) {
          movedItem.parent += 1
        } else {
          return
        }
      }
    }

    if (
      resultX >= 40 &&
      movedItem?.parent !== undefined &&
      movedItem.parent !== null &&
      movedItem.parent > 0 &&
      newIndex !== undefined
    ) {
      for (let i = newIndex + 1; i < updatedItems.length; i++) {
        const currentItem = updatedItems[i]
        if (currentItem.parent !== null && currentItem.parent > 0) {
          currentItem.parent += 1
        }
      }
    }

    if (resultX <= -40) {
      if (oldIndex !== undefined && movedItem && movedItem.parent !== null) {
        movedItem.parent -= 1
      }
    }

    if (
      resultX <= -40 &&
      movedItem?.parent !== undefined &&
      movedItem.parent !== null &&
      movedItem.parent >= 0 &&
      newIndex !== undefined
    ) {
      for (let i = newIndex + 1; i < updatedItems.length; i++) {
        const currentItem = updatedItems[i]
        if (currentItem.parent !== null && currentItem.parent > 1) {
          currentItem.parent -= 1
        }
      }
    }

    if (newIndex !== oldIndex && newIndex !== undefined && oldIndex !== undefined) {
      const removedItem = updatedItems.splice(oldIndex, 1)[0]
      updatedItems.splice(newIndex, 0, removedItem)

      updatedItems.forEach((item, index) => {
        item.order = index + 1
      })

      if (newIndex > 0) {
        const upperItem = updatedItems[newIndex - 1]
        if (
          upperItem &&
          removedItem &&
          removedItem.parent !== null &&
          upperItem.parent !== null &&
          removedItem.parent > upperItem.parent + 1
        ) {
          removedItem.parent = upperItem.parent + 1
        }
      }

      for (let i = newIndex + 1; i < updatedItems.length; i++) {
        const currentItem = updatedItems[i]

        if (currentItem.parent !== null && removedItem.parent !== null && currentItem.parent > removedItem.parent) {
          currentItem.parent = removedItem.parent + 1

          updatedItems.splice(i, 1)
          updatedItems.splice(newIndex + 1, 0, currentItem)
        }
      }

      updatedItems.forEach((item, index) => {
        item.order = index + 1
      })

      setMenuItems(updatedItems)
    }
  }

  const handleCheckboxChange = (e: any, item: string) => {
    const { checked } = e.target
    setCheckedItems(prev => {
      if (checked) {
        return [...prev, item]
      } else {
        return prev.filter(i => i !== item)
      }
    })
  }

  const handlePageSelectAll = () => {
    const allPageNames = mostRecentPages.map(page => page.name)
    const areAllChecked = allPageNames.every(name => checkedItems.includes(name))

    if (areAllChecked) {
      setCheckedItems([])
    } else {
      setCheckedItems(allPageNames)
    }
    setSelectedPosts(new Set())
    setSelectedCategories(new Set())
  }

  const handlePostSelectAll = () => {
    const allPostNames = mostRecentPosts.map(post => post.name)
    const areAllChecked = allPostNames.every(name => checkedItems.includes(name))

    if (areAllChecked) {
      setCheckedItems([])
    } else {
      setCheckedItems(allPostNames)
    }
    setSelectedPages(new Set())
    setSelectedCategories(new Set())
  }

  const handleCategorySelectAll = () => {
    const allCategoryNames = mostRecentCategories.map(category => category.name)
    const areAllChecked = allCategoryNames.every(name => checkedItems.includes(name))

    if (areAllChecked) {
      setCheckedItems([])
    } else {
      setCheckedItems(allCategoryNames)
    }
    setSelectedPages(new Set())
    setSelectedPosts(new Set())
  }

  const togglePagesExpand = () => {
    setExpandedPages(!expandedPages)
    if (!expandedPages) {
      setExpandedPosts(false)
      setExpandedCategories(false)
      setExpandedCustomlink(false)
    }
  }

  const togglePostsExpand = () => {
    setExpandedPosts(!expandedPosts)
    if (!expandedPosts) {
      setExpandedPages(false)
      setExpandedCategories(false)
      setExpandedCustomlink(false)
    }
  }

  const toggleCategoryExpand = () => {
    setExpandedCategories(!expandedCategories)
    if (!expandedCategories) {
      setExpandedPages(false)
      setExpandedPosts(false)
      setExpandedCustomlink(false)
    }
  }

  const toggleCustomLinkExpand = () => {
    setExpandedCustomlink(!expandedCustomlink)
    if (!expandedCustomlink) {
      setExpandedPages(false)
      setExpandedPosts(false)
      setExpandedCategories(false)
    }
  }

  const handleMenuNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMenuName(e.target.value)
  }

  const cancel: PopconfirmProps["onCancel"] = () => {}

  const handleCancel = (itemId: string) => {
    if (expandedItem === itemId) {
      setExpandedItem(null)
    }
  }

  const handleCreateNewMenu = async () => {
    setIsCreatingNewMenu(true)
    setSelectedMenu(null)
    setMenuItems([])
    setMenuName("")
    setIsLoadingLanguage(true)

    try {
      await new Promise(resolve => setTimeout(resolve, 500))
      setLanguage(localStorage.getItem("language") || "")
    } catch (error) {
      console.error(t("Error updating language:"), error)
      message.error(t("Failed to update language"))
    } finally {
      setIsLoadingLanguage(false)
    }
  }

  const handleUpdate = (id: number, newTitle: string, newUrl: string) => {
    setMenuItems(prevItems => prevItems.map(item => (item.id === id ? { ...item, name: newTitle, url: newUrl } : item)))
  }

  useEffect(() => {
    if (expandedPages && subTab === "tabMos") {
      fetchPages()
      fetchPosts()
      fetchCategories()
      fetchMenuPreferences()
    }
  }, [])

  useEffect(() => {
    const savedMenuId = localStorage.getItem("selectedMenu")

    if (savedMenuId) {
      handleSelectMenuChange(savedMenuId)
    }
  }, [menuPreference])

  return (
    <div className="px-4">
      <div className="flex py-1 items-center justify-between">
        <div className="flex space-x-4 items-center mt-3 mb-3">
          <Title level={2} className="m-0">
            Menus
          </Title>
        </div>
      </div>
      <div>
        {mainTab === "tab1" && (
          <div className="flex items-center p-[16px] border border-1 rounded-[8px] w-full pl-[20px]">
            <span style={{ marginRight: 20 }}>{t("Select a menu to edit:")}</span>
            <Select value={selectedMenu} style={{ width: 200 }} onChange={handleSelectMenuChange}>
              {Array.isArray(menuPreference) &&
                menuPreference.map(item => (
                  <Select.Option key={item.id} value={item.name}>
                    {item.attributes.name} - <span className="uppercase">{item.attributes.locale}</span>
                  </Select.Option>
                ))}
            </Select>
            <div className="flex items-center gap-2 md:gap-4 mt-2 md:mt-0 ml-0 md:ml-4">
              <p className="px-1">{t("Or")}</p>
              <a
                className="underline-offset-1 hover:text-blue-400 border border-gray-300 py-2 px-4 rounded-md hover:border-blue-400 transition duration-200 ease-in-out"
                onClick={handleCreateNewMenu}
              >
                {t("Create a new menu")}
              </a>
            </div>
          </div>
        )}
      </div>
      <div>
        {mainTab === "tab1" && (
          <div className="flex flex-col lg:flex-row">
            <div className="w-[25%] border-gray-300 divide-y divide-gray-300">
              <div>
                <Title level={3} className="py-5">
                  {t("Add menu items")}
                </Title>
              </div>
              <LanguageItem
                isLoading={isLoadingLanguage}
                defaultValue={language}
                disable={!isCreatingNewMenu}
                onChange={language => {
                  setLanguage(language)
                }}
              />
              <PagesItem
                expandedPages={expandedPages}
                togglePagesExpand={togglePagesExpand}
                subTab={subTab}
                handleSubTabChange={handleSubTabChange}
                mostRecentPages={mostRecentPages}
                checkedItems={checkedItems}
                handleCheckboxChange={handleCheckboxChange}
                handlePageSelectAll={handlePageSelectAll}
                addToMenu={addToMenu}
              />

              <PostsItem
                expandedPosts={expandedPosts}
                togglePostsExpand={togglePostsExpand}
                subTab={subTab}
                handleSubTabChange={handleSubTabChange}
                mostRecentPosts={mostRecentPosts}
                checkedItems={checkedItems}
                handleCheckboxChange={handleCheckboxChange}
                handlePostSelectAll={handlePostSelectAll}
                addToMenu={addToMenu}
              />
              <CategoriesItem
                expandedCategories={expandedCategories}
                toggleCategoryExpand={toggleCategoryExpand}
                subTab={subTab}
                handleSubTabChange={handleSubTabChange}
                mostRecentCategories={mostRecentCategories}
                checkedItems={checkedItems}
                handleCheckboxChange={handleCheckboxChange}
                handleCategorySelectAll={handleCategorySelectAll}
                addToMenu={addToMenu}
              />
              <CustomLinksItem
                expandedCustomlink={expandedCustomlink}
                toggleCustomLinkExpand={toggleCustomLinkExpand}
                subTab={subTab}
                handleSubTabChange={handleSubTabChange}
                form={form}
                addToMenu={addToMenu}
              />
            </div>
            <div className="w-[75%] ml-[2%]">
              <div>
                <Title level={3} className="py-5">
                  {t("Menu structure")}
                </Title>
              </div>
              <div className="border rounded-[9px] ">
                <div className="flex bg-[#E5E5E580] p-[10px] pt-2 rounded-t-[8px] relative items-center pl-6">
                  <p className="w-[10%]">{t("Menu Name")}</p>
                  <Input
                    onChange={handleMenuNameChange}
                    value={menuName}
                    placeholder={t("Menu Name")}
                    className="items-center w-[20%]"
                  />
                </div>
                <div className="px-2 pl-6">
                  <div className="p-2 "></div>
                  <div>
                    {menuItems.length > 0 && !isCreatingNewMenu && (
                      <ReactSortable
                        list={menuItems}
                        setList={setMenuItems}
                        group={{ name: "menu", pull: true, put: true }}
                        animation={350}
                        onEnd={handleEnd}
                        onStart={handleStart}
                        fallbackOnBody={true}
                        swapThreshold={0.65}
                        forceFallback={true}
                      >
                        {menuItems
                          .sort((a, b) => a.order - b.order)
                          .map(item => (
                            <Card
                              className="w-[60%] items-center mb-2 cursor-move hover:bg-gray-100"
                              key={item.id}
                              style={{ marginLeft: `${(item.parent ?? 0) * 40}px`, borderRadius: 4 }}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <h2>{item.name || "No Title"}</h2>
                                </div>
                                <div className="flex">
                                  <Button
                                    type="text"
                                    onClick={() => toggleExpand(item.id.toString())}
                                    icon={
                                      expandedItem === item.id.toString() ? <CaretUpOutlined /> : <CaretDownOutlined />
                                    }
                                  />
                                </div>
                              </div>
                              {expandedItem === item.id.toString() && (
                                <div>
                                  <ContentHomePage
                                    title={item.name}
                                    url={item.url}
                                    onRemove={() => handleRemove(item.id)}
                                    onCancel={() => handleCancel(item.id.toString())}
                                    onUpdate={(newTitle, newUrl) => handleUpdate(item.id, newTitle, newUrl)}
                                  />
                                </div>
                              )}
                            </Card>
                          ))}
                      </ReactSortable>
                    )}
                  </div>
                </div>
                <div className="rounded-b-[8px] w-full p-3 bg-gray-100 sticky bottom-0 flex items-center">
                  <Button
                    type="primary"
                    className="bg-blue-600 text-white rounded-[8px] shadow-none"
                    onClick={saveMenu}
                  >
                    {t("Save Menu")}
                  </Button>
                  <Popconfirm
                    title={t("Delete the task")}
                    description={t("Are you sure to delete this task?")}
                    onConfirm={handleDeleteMenu}
                    onCancel={cancel}
                    okText={t("Yes")}
                    cancelText={t("No")}
                    className="bg-red-500 pl-4 cursor-pointer "
                  >
                    <Button danger type="primary" className="ml-2 rounded-[8px] shadow-none ">
                      <span>{t("Delete Menu")}</span>
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default MenusListing
